<template>
  <div
    ref="editor"
    contenteditable="true"
    :data-placeholder="placeholder"
    class="pfs-rich-editor"
    @input="onInput"
    @keydown="onKeydown"
    @paste.prevent="onPaste"
    @dragstart="onDragStart"
    @dragenter.prevent="onDragEnter"
    @dragover.prevent="onDragOver"
    @dragleave="onDragLeave"
    @drop.prevent="onDrop"
    @selectstart="onSelectStart"
    @focus="$emit('focus')"
    @blur="onBlur"
  ></div>
</template>

<script>
const TOKEN_RE = /(\[[^\]]+\])/g

function stripBrackets(token) {
  return token.replace(/^\[/, '').replace(/\]$/, '')
}

function toHtml(text) {
  if (!text) return ''
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(TOKEN_RE, function(token) {
      var escaped = token.replace(/"/g, '&quot;')
      var labelText = stripBrackets(token).replace(/"/g, '&quot;')
      // \u200B after each chip gives the cursor a landing spot between adjacent chips
      return '<span class="pfs-token-chip" contenteditable="false" draggable="true" data-token="' + escaped + '"><span class="pfs-token-chip-label">' + labelText + '</span></span>\u200B'
    })
}

// Block-level tags that browsers create on Enter inside a contenteditable.
// Each one represents a new line, so we must insert \n before recursing into
// them (unless we're at the very start of the output).
const BLOCK_TAGS = new Set(['DIV', 'P', 'LI', 'BLOCKQUOTE'])

function toPlain(el) {
  var result = ''
  var nodes = Array.prototype.slice.call(el.childNodes)
  for (var i = 0; i < nodes.length; i++) {
    var node = nodes[i]
    if (node.nodeType === 3 /* TEXT_NODE */) {
      // Strip zero-width spaces used as cursor landing zones between chips
      result += node.textContent.replace(/\u200B/g, '')
    } else if (node.nodeType === 1 /* ELEMENT_NODE */) {
      if (node.dataset && node.dataset.token) {
        result += node.dataset.token
      } else if (node.tagName === 'BR') {
        result += '\n'
      } else if (BLOCK_TAGS.has(node.tagName)) {
        // Block elements represent line breaks. Add \n before the block's
        // content unless we're at the start (avoids a spurious leading newline).
        if (result.length > 0 && !result.endsWith('\n')) {
          result += '\n'
        }
        result += toPlain(node)
      } else {
        result += toPlain(node)
      }
    }
  }
  return result
}

export default {
  name: 'PfsRichEditor',

  props: {
    value: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
  },

  data: function() {
    return {
      lastEmitted: this.value || '',
    }
  },

  watch: {
    value: function(newVal) {
      var norm = newVal || ''
      if (norm !== this.lastEmitted) {
        this.lastEmitted = norm
        this._setHtml(norm)
      }
    },
  },

  mounted: function() {
    this._setHtml(this.value || '')
    // Persistent fixed-position caret indicator for external drags.
    // Using a single reusable element with CSS updates (no DOM mutations in the
    // editor) avoids the GC pressure that caused periodic 2-second freezes.
    var c = document.createElement('div')
    c.style.cssText = 'position:fixed;width:2px;background:#495057;pointer-events:none;display:none;z-index:9999;border-radius:1px;'
    document.body.appendChild(c)
    this._overlayCaret = c
  },

  beforeDestroy: function() {
    if (this._overlayCaret && this._overlayCaret.parentNode) {
      this._overlayCaret.parentNode.removeChild(this._overlayCaret)
    }
  },

  methods: {
    // If `range` lands inside a chip, redirect it to before or after that chip
    // based on which horizontal half of the chip the cursor (clientX) is over.
    // Pass `excludeChip` for internal drags so the source chip is ignored.
    _antiNest: function(range, el, clientX, excludeChip) {
      if (!range) return range
      var node = range.startContainer
      while (node && node !== el) {
        if (node.nodeType === 1 && node.dataset && node.dataset.token && node !== excludeChip) {
          var rect = node.getBoundingClientRect()
          var goAfter = clientX >= rect.left + rect.width / 2
          range = document.createRange()
          if (goAfter) {
            range.setStartAfter(node)
          } else {
            range.setStartBefore(node)
          }
          range.collapse(true)
          break
        }
        node = node.parentNode
      }
      return range
    },

    onBlur: function() {
      // Save the current caret range so insertToken can use it even after focus
      // has moved away (e.g. when clicking a selector chip)
      var el = this.$refs.editor
      var sel = window.getSelection()
      if (sel && sel.rangeCount > 0 && el && el.contains(sel.getRangeAt(0).startContainer)) {
        this._savedRange = sel.getRangeAt(0).cloneRange()
      }
      this.$emit('blur')
    },

    _setHtml: function(text) {
      var el = this.$refs.editor
      if (!el) return
      this._initializing = true
      try {
        el.innerHTML = toHtml(text)
      } catch (e) { /* ignore */ }
      var self = this
      this.$nextTick(function() { self._initializing = false })
    },

    onInput: function() {
      if (this._initializing) return
      var el = this.$refs.editor
      if (!el) return
      var plain = toPlain(el)
      if (plain === this.lastEmitted) return
      this.lastEmitted = plain
      this.$emit('input', plain)
    },

    // Like onInput, but defers the $emit to the next macrotask so the browser
    // can paint the editor's DOM changes before the parent's reactive update
    // cycle blocks the main thread. Used after drag-drop where the parent
    // re-render can be expensive on large templates.
    onInputDeferred: function() {
      var el = this.$refs.editor
      if (!el) return
      var plain = toPlain(el)
      this.lastEmitted = plain
      var self = this
      setTimeout(function() {
        if (self._isDestroyed) return
        self.$emit('input', plain)
      }, 0)
    },

    onKeydown: function(e) {
      if (e.key === 'Enter') {
        // The browser mutates the DOM after keydown but doesn't reliably fire
        // an `input` event for the structural change (new div/p block). Defer
        // so the DOM is settled before we serialize.
        var self = this
        setTimeout(function() { self.onInput() }, 0)
        return
      }
      if (e.key !== 'Backspace') return
      var sel = window.getSelection()
      if (!sel || sel.rangeCount === 0) return
      var range = sel.getRangeAt(0)
      if (!range.collapsed) return
      var el = this.$refs.editor
      // If the node immediately before the caret is a chip, delete it cleanly
      var prev = null
      if (range.startContainer === el) {
        prev = el.childNodes[range.startOffset - 1] || null
      } else if (range.startOffset === 0) {
        prev = range.startContainer.previousSibling
      }
      if (prev && prev.nodeType === 1 && prev.dataset && prev.dataset.token) {
        e.preventDefault()
        prev.parentNode.removeChild(prev)
        this.onInput()
      }
    },

    onPaste: function(e) {
      var text = (e.clipboardData || window.clipboardData).getData('text/plain')
      if (!text) return
      var sel = window.getSelection()
      if (!sel || sel.rangeCount === 0) return
      var range = sel.getRangeAt(0)
      range.deleteContents()
      // Parse pasted text into a fragment so tokens become chips
      var tmp = document.createElement('div')
      tmp.innerHTML = toHtml(text)
      var frag = document.createDocumentFragment()
      var children = Array.prototype.slice.call(tmp.childNodes)
      for (var i = 0; i < children.length; i++) {
        frag.appendChild(children[i])
      }
      var lastChild = frag.lastChild
      range.insertNode(frag)
      if (lastChild) {
        var after = document.createRange()
        after.setStartAfter(lastChild)
        after.collapse(true)
        sel.removeAllRanges()
        sel.addRange(after)
      }
      this.onInput()
    },

    insertToken: function(tokenName) {
      var el = this.$refs.editor
      if (!el) return
      var chip = document.createElement('span')
      chip.contentEditable = 'false'
      chip.draggable = true
      chip.dataset.token = tokenName
      chip.className = 'pfs-token-chip'
      var label = document.createElement('span')
      label.className = 'pfs-token-chip-label'
      label.textContent = stripBrackets(tokenName)
      chip.appendChild(label)

      var sel = window.getSelection()
      // Use the live selection if it's inside the editor, otherwise fall back
      // to the range saved when the editor last lost focus
      var range = null
      if (sel && sel.rangeCount > 0 && el.contains(sel.getRangeAt(0).commonAncestorContainer)) {
        range = sel.getRangeAt(0)
      } else if (this._savedRange && el.contains(this._savedRange.startContainer)) {
        range = this._savedRange
      }

      var spacer = document.createTextNode('\u200B')
      if (range) {
        range.deleteContents()
        range.insertNode(spacer)
        range.insertNode(chip)
      } else {
        el.appendChild(chip)
        el.appendChild(spacer)
      }

      // Place cursor in the spacer after the chip
      el.focus()
      var after = document.createRange()
      after.setStart(spacer, 1)
      after.collapse(true)
      if (sel) {
        sel.removeAllRanges()
        sel.addRange(after)
      }
      this._savedRange = after.cloneRange()
      this.onInput()
    },

    onSelectStart: function(e) {
      // When the user starts a drag from a chip, block text-selection so the
      // browser routes the gesture to drag-and-drop instead.
      if (e.target && e.target.dataset && e.target.dataset.token) {
        e.preventDefault()
      }
    },

    onDragStart: function(e) {
      var target = e.target
      if (!target.dataset || !target.dataset.token) {
        e.preventDefault()
        return
      }
      this._draggingChip = target
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', target.dataset.token)

      // Build a ghost image: the chip clone + transparent padding below it.
      // The cursor hotspot sits in the padding so the chip always renders above
      // the cursor and doesn't obscure it while dragging.
      var gap = 28
      var ghost = document.createElement('div')
      ghost.style.cssText = 'position:fixed;top:-9999px;left:-9999px;padding-bottom:' + gap + 'px;'
      ghost.appendChild(target.cloneNode(true))
      document.body.appendChild(ghost)
      var hotX = Math.round(target.offsetWidth / 2)
      var hotY = target.offsetHeight + Math.round(gap / 2)
      e.dataTransfer.setDragImage(ghost, hotX, hotY)
      // Remove the ghost after the browser has captured it
      setTimeout(function() { document.body.removeChild(ghost) }, 0)
    },

    onDragEnter: function(e) {
      // Setting dropEffect here in addition to dragover helps some browsers
      // recognise the editor as a valid drop target consistently.
      if (!this._draggingChip) {
        e.dataTransfer.dropEffect = 'copy'
      }
    },

    onDragOver: function(e) {
      var el = this.$refs.editor
      if (this._draggingChip) {
        // Internal drag (chip already in the editor) — show the live text caret
        // so the user can see exactly where the chip will land.
        e.dataTransfer.dropEffect = 'move'
        var range = null
        if (document.caretRangeFromPoint) {
          range = document.caretRangeFromPoint(e.clientX, e.clientY)
        } else if (document.caretPositionFromPoint) {
          var pos = document.caretPositionFromPoint(e.clientX, e.clientY)
          if (pos) {
            range = document.createRange()
            range.setStart(pos.offsetNode, pos.offset)
          }
        }
        if (range) {
          var sel = window.getSelection()
          if (sel) {
            sel.removeAllRanges()
            sel.addRange(range)
          }
        }
      } else {
        // External drag — suppress Chrome's native drag-over caret via CSS
        // (caret-color: transparent on .pfs-drag-target) instead of toggling
        // contentEditable, which is very expensive to restore on drop.
        e.dataTransfer.dropEffect = 'copy'
        if (!el.classList.contains('pfs-drag-target')) {
          el.classList.add('pfs-drag-target')
        }

        // Throttle to one update per animation frame.
        if (this._dragOverRAF) return
        var self = this
        var clientX = e.clientX
        var clientY = e.clientY
        this._dragOverRAF = requestAnimationFrame(function() {
          self._dragOverRAF = null

          var extRange = null
          if (document.caretRangeFromPoint) {
            extRange = document.caretRangeFromPoint(clientX, clientY)
          } else if (document.caretPositionFromPoint) {
            var cPos = document.caretPositionFromPoint(clientX, clientY)
            if (cPos) {
              extRange = document.createRange()
              extRange.setStart(cPos.offsetNode, cPos.offset)
              extRange.collapse(true)
            }
          }

          if (extRange && el.contains(extRange.startContainer)) {
            extRange = self._antiNest(extRange, el, clientX, null)
            // Save for onDrop — no DOM mutation needed
            self._extDropRange = extRange
            // Position the overlay caret. getBoundingClientRect on a collapsed
            // Range is unreliable when the range is between inline-block elements
            // (setStartBefore/After a chip), so fall back to clientX if the rect
            // is outside the editor bounds or has zero height.
            if (self._overlayCaret) {
              var editorRect = el.getBoundingClientRect()
              var rect = extRange.getBoundingClientRect()
              var caretLeft, caretTop, caretHeight
              if (rect && rect.height > 0 &&
                  rect.left >= editorRect.left && rect.left <= editorRect.right) {
                caretLeft = rect.left
                caretTop = rect.top
                caretHeight = rect.height
              } else {
                // Fallback: derive position from the range's container+offset
                // (set by _antiNest to before/after a chip boundary).
                // Using clientX here would show the caret inside the chip, which
                // is misleading since the drop will land at the chip edge.
                var container = extRange.startContainer
                var offset = extRange.startOffset
                caretHeight = 20
                caretTop = clientY - caretHeight / 2
                if (container.nodeType === 1 /* ELEMENT */) {
                  var nodeBefore = offset > 0 ? container.childNodes[offset - 1] : null
                  var nodeAfter = container.childNodes[offset] || null
                  // Skip text nodes (ZWSP spacers) — only elements have getBoundingClientRect
                  var elBefore = (nodeBefore && nodeBefore.nodeType === 1) ? nodeBefore : null
                  var elAfter  = (nodeAfter  && nodeAfter.nodeType  === 1) ? nodeAfter  : null
                  var refEl = elBefore || elAfter
                  if (refEl) {
                    var refRect = refEl.getBoundingClientRect()
                    caretTop = refRect.top
                    caretHeight = refRect.height
                    // After a chip → its right edge; before a chip → its left edge
                    caretLeft = elBefore
                      ? elBefore.getBoundingClientRect().right
                      : elAfter.getBoundingClientRect().left
                  } else {
                    caretLeft = clientX
                    caretTop = clientY - caretHeight / 2
                  }
                } else {
                  caretLeft = clientX
                  caretTop = clientY - caretHeight / 2
                }
              }
              self._overlayCaret.style.left = caretLeft + 'px'
              self._overlayCaret.style.top = caretTop + 'px'
              self._overlayCaret.style.height = caretHeight + 'px'
              self._overlayCaret.style.display = 'block'
            }
          }
        })
      }
    },

    onDragLeave: function(e) {
      var el = this.$refs.editor
      if (!el.contains(e.relatedTarget)) {
        if (this._dragOverRAF) {
          cancelAnimationFrame(this._dragOverRAF)
          this._dragOverRAF = null
        }
        if (this._overlayCaret) this._overlayCaret.style.display = 'none'
        this._extDropRange = null
        el.classList.remove('pfs-drag-target')
        var sel = window.getSelection()
        if (sel) sel.removeAllRanges()
      }
    },

    onDrop: function(e) {
      var el = this.$refs.editor
      if (this._dragOverRAF) {
        cancelAnimationFrame(this._dragOverRAF)
        this._dragOverRAF = null
      }
      if (this._overlayCaret) this._overlayCaret.style.display = 'none'
      el.classList.remove('pfs-drag-target')

      // Use the range saved by the last onDragOver RAF — it's the accurate
      // insertion point and avoids a layout-shifting re-query after DOM changes.
      var extDropRange = this._extDropRange || null
      this._extDropRange = null

      var sel = window.getSelection()
      var range = (sel && sel.rangeCount > 0) ? sel.getRangeAt(0).cloneRange() : null

      // If the drop landed inside a chip, redirect before/after based on
      // which half of the chip the cursor is over
      range = this._antiNest(range, el, e.clientX, this._draggingChip)

      if (this._draggingChip) {
        // Internal drag — move existing chip
        var chip = this._draggingChip
        this._draggingChip = null

        var nextSib = chip.nextSibling
        if (nextSib && nextSib.nodeType === 3 && nextSib.textContent === '\u200B') {
          nextSib.parentNode.removeChild(nextSib)
        }
        chip.parentNode.removeChild(chip)

        var spacer = document.createTextNode('\u200B')
        if (range && el.contains(range.startContainer)) {
          range.insertNode(spacer)
          range.insertNode(chip)
        } else {
          el.appendChild(chip)
          el.appendChild(spacer)
        }

        if (sel) {
          var after = document.createRange()
          after.setStart(spacer, 1)
          after.collapse(true)
          sel.removeAllRanges()
          sel.addRange(after)
        }
      } else {
        // External drag — new chip from the selector panel.
        // Prefer the position captured from the marker (set accurately by onDragOver).
        // Fall back to caretRangeFromPoint only if no marker was present.
        var tokenName = e.dataTransfer.getData('application/x-pfs-token')
        if (!tokenName) return

        if (extDropRange && el.contains(extDropRange.startContainer)) {
          range = extDropRange
        } else if (!range || !el.contains(range.startContainer)) {
          if (document.caretRangeFromPoint) {
            range = document.caretRangeFromPoint(e.clientX, e.clientY)
          } else if (document.caretPositionFromPoint) {
            var cPos = document.caretPositionFromPoint(e.clientX, e.clientY)
            if (cPos) {
              range = document.createRange()
              range.setStart(cPos.offsetNode, cPos.offset)
            }
          }
        }

        // Anti-nesting: redirect before/after the chip based on cursor position
        range = this._antiNest(range, el, e.clientX, null)

        var extChip = document.createElement('span')
        extChip.contentEditable = 'false'
        extChip.draggable = true
        extChip.dataset.token = tokenName
        extChip.className = 'pfs-token-chip'
        var extLabel = document.createElement('span')
        extLabel.className = 'pfs-token-chip-label'
        extLabel.textContent = stripBrackets(tokenName)
        extChip.appendChild(extLabel)
        var extSpacer = document.createTextNode('\u200B')

        if (range && el.contains(range.startContainer)) {
          range.insertNode(extSpacer)
          range.insertNode(extChip)
        } else {
          el.appendChild(extChip)
          el.appendChild(extSpacer)
        }

        if (sel) {
          var extAfter = document.createRange()
          extAfter.setStart(extSpacer, 1)
          extAfter.collapse(true)
          sel.removeAllRanges()
          sel.addRange(extAfter)
        }
      }

      el.focus()
      this.onInputDeferred()

      // Force a layout/paint flush so the inserted chip becomes visible
      // immediately, even when the user drops without moving the mouse.
      // Without this, Chrome on macOS sometimes defers the paint until the
      // next mouse movement.
      void el.offsetHeight
    },
  },
}
</script>

<style>
.pfs-rich-editor {
  display: block;
  width: 100%;
  min-height: 38px;
  padding: 6px 12px;
  font-size: 14px;
  line-height: 1.5;
  color: #495057;
  background-color: #fff;
  border: 1px solid #ced4da;
  border-radius: 4px;
  outline: none;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-y: auto;
  box-sizing: border-box;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.pfs-rich-editor:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* When a table row contains a rich editor, keep all cells top-aligned so the
   label doesn't drift to the vertical centre when Available Fields expands. */
tr:has(.pfs-rich-editor) td {
  vertical-align: top;
}

.pfs-rich-editor.pfs-drag-target {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  caret-color: transparent;
}

.pfs-rich-editor:empty::before {
  content: attr(data-placeholder);
  color: #6c757d;
  pointer-events: none;
}

.pfs-token-chip-label {
  pointer-events: none;
}

.pfs-token-chip {
  display: inline-block;
  padding: 1px 7px;
  margin: 0 2px;
  background: #e8f0fe;
  border: 1px solid #c5d5f7;
  border-radius: 12px;
  font-family: monospace;
  font-size: 12px;
  color: #1a56db;
  white-space: nowrap;
  user-select: all;
  cursor: grab;
}

.pfs-token-chip:active {
  cursor: grabbing;
  vertical-align: baseline;
  line-height: 1.4;
}
</style>
